import React, { useState } from 'react';
import { Card, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronLeft, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import '../common.css';

// Venue type options
const venueTypeOptions = [
  { id: "performance", label: "Performance venues/theaters" },
  { id: "conference", label: "Conference centers" },
  { id: "hotels", label: "Hotels" },
  { id: "educational", label: "Universities/educational" },
  { id: "outdoor", label: "Outdoor spaces" },
  { id: "sports", label: "Sports facilities" },
  { id: "community", label: "Community centers" },
  { id: "restaurants", label: "Restaurants/bars" },
  { id: "corporate", label: "Corporate facilities" },
  { id: "museums", label: "Museums/galleries" },
  { id: "other", label: "Other" }
];

// Seating style options
const seatingStyleOptions = [
  { id: "theater", label: "Theater/rows" },
  { id: "classroom", label: "Classroom/tables" },
  { id: "banquet", label: "Banquet/round tables" },
  { id: "standing", label: "Standing/general admission" },
  { id: "mixed", label: "Mixed formats" },
  { id: "other", label: "Other" }
];

// Technical requirement options
const technicalFields = [
  { id: "sound", label: "Sound system specifications", placeholder: "e.g., 32-channel mixing board, line array speakers, etc." },
  { id: "lighting", label: "Lighting requirements", placeholder: "e.g., DMX control, spotlights, stage washes, etc." },
  { id: "stage", label: "Stage/platform needs", placeholder: "e.g., 40' x 30' stage, 3' height, etc." },
  { id: "video", label: "Video/projection needs", placeholder: "e.g., 4K projector, 16:9 screen, etc." },
  { id: "internet", label: "Internet/connectivity requirements", placeholder: "e.g., 100Mbps dedicated line, WiFi for 500 users, etc." },
  { id: "power", label: "Power requirements", placeholder: "e.g., 200A 3-phase power, multiple 20A circuits, etc." },
  { id: "other", label: "Other technical needs", placeholder: "Any other specific technical requirements" }
];

// Additional spaces needed options
const additionalSpacesOptions = [
  { id: "greenroom", label: "Green room/preparation area" },
  { id: "registration", label: "Registration/check-in area" },
  { id: "breakout", label: "Breakout rooms" },
  { id: "backstage", label: "Backstage area" },
  { id: "vip", label: "VIP/private areas" },
  { id: "exhibition", label: "Exhibition/vendor space" },
  { id: "catering", label: "Catering/food service area" },
  { id: "storage", label: "Storage space" },
  { id: "other", label: "Other" }
];

// Venue amenities options
const venueAmenitiesOptions = [
  { id: "accessibility", label: "Accessibility features required" },
  { id: "parking", label: "Parking capacity" },
  { id: "transportation", label: "Public transportation access" },
  { id: "loading", label: "Loading dock/equipment access" },
  { id: "tech-staff", label: "In-house technical staff" },
  { id: "catering", label: "In-house catering" },
  { id: "alcohol", label: "Alcohol service capability" },
  { id: "accommodation", label: "Overnight accommodation on-site" },
  { id: "green", label: "Green/sustainability certifications" },
  { id: "other", label: "Other" }
];

const VenueTechnical = ({ formData, onInputChange, onNext, onPrev, error }) => {
  const [validationError, setValidationError] = useState('');
  
  // Handle checkbox changes for multi-select options
  const handleMultiSelectChange = (field, value, isChecked) => {
    const currentValues = formData[field] || [];
    let newValues;
    
    if (isChecked) {
      newValues = [...currentValues, value];
    } else {
      newValues = currentValues.filter(item => item !== value);
    }
    
    onInputChange({ [field]: newValues });
    
    // Clear validation error if the required field is now filled
    if (field === 'venueTypes' && newValues.length > 0) {
      setValidationError('');
    }
  };

  // Handle input changes for text fields
  const handleInputChange = (field, value) => {
    onInputChange({ [field]: value });
  };

  // Check if a value is selected in a multi-select list
  const isSelected = (field, value) => {
    return (formData[field] || []).includes(value);
  };
  
  // Validate form before proceeding to next step
  const validateForm = () => {
    // Check if required fields are filled
    if (!formData.venueTypes || formData.venueTypes.length === 0) {
      setValidationError('Please select at least one venue type');
      return false;
    }
    
    return true;
  };
  
  // Handle next button click
  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  return (
    <Card className="dark-main-card text-white mb-4">
      <Card.Header className="dark-card-header border-bottom">
        <h4 className="mb-0 text-white">Specify Your Venue Needs</h4>
      </Card.Header>
      <Card.Body className="p-4">
        {error && <Alert variant="danger" className="dark-alert mb-4">{error}</Alert>}
        {validationError && (
          <Alert variant="warning" className="dark-alert mb-4">
            <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
            {validationError}
          </Alert>
        )}
        
        <Form>
          {/* Venue Type Preferences */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Venue Type Preferences <span className="text-danger">*</span></h5>
            
            <Row>
              {venueTypeOptions.slice(0, venueTypeOptions.length - 1).map((option) => (
                <Col md={6} key={option.id} className="mb-2">
                  <Form.Check
                    type="checkbox"
                    id={`venue-type-${option.id}`}
                    className="dark-checkbox"
                    label={option.label}
                    checked={isSelected('venueTypes', option.id)}
                    onChange={(e) => handleMultiSelectChange('venueTypes', option.id, e.target.checked)}
                  />
                </Col>
              ))}
            </Row>
            
            <Row className="mt-2">
              <Col md={6} className="mb-2">
                <Form.Check
                  type="checkbox"
                  id="venue-type-other"
                  className="dark-checkbox"
                  label="Other"
                  checked={isSelected('venueTypes', 'other')}
                  onChange={(e) => handleMultiSelectChange('venueTypes', 'other', e.target.checked)}
                />
              </Col>
              <Col md={6}>
                {isSelected('venueTypes', 'other') && (
                  <Form.Control
                    type="text"
                    className="dark-input"
                    placeholder="Specify other venue type"
                    value={formData.venueTypeOther || ''}
                    onChange={(e) => handleInputChange('venueTypeOther', e.target.value)}
                  />
                )}
              </Col>
            </Row>
          </div>
          
          {/* Capacity Requirements */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Capacity Requirements</h5>
            
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label className="text-white">Minimum capacity</Form.Label>
                  <Form.Control
                    type="number"
                    className="dark-input"
                    min="0"
                    placeholder="Minimum number of attendees"
                    value={formData.minCapacity || ''}
                    onChange={(e) => handleInputChange('minCapacity', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label className="text-white">Maximum capacity</Form.Label>
                  <Form.Control
                    type="number"
                    className="dark-input"
                    min="0"
                    placeholder="Maximum number of attendees"
                    value={formData.maxCapacity || ''}
                    onChange={(e) => handleInputChange('maxCapacity', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Seating style</Form.Label>
              <Row>
                {seatingStyleOptions.slice(0, seatingStyleOptions.length - 1).map((option) => (
                  <Col md={6} key={option.id} className="mb-2">
                    <Form.Check
                      type="checkbox"
                      id={`seating-${option.id}`}
                      className="dark-checkbox"
                      label={option.label}
                      checked={isSelected('seatingStyles', option.id)}
                      onChange={(e) => handleMultiSelectChange('seatingStyles', option.id, e.target.checked)}
                    />
                  </Col>
                ))}
              </Row>
              
              <Row className="mt-2">
                <Col md={6} className="mb-2">
                  <Form.Check
                    type="checkbox"
                    id="seating-other"
                    className="dark-checkbox"
                    label="Other"
                    checked={isSelected('seatingStyles', 'other')}
                    onChange={(e) => handleMultiSelectChange('seatingStyles', 'other', e.target.checked)}
                  />
                </Col>
                <Col md={6}>
                  {isSelected('seatingStyles', 'other') && (
                    <Form.Control
                      type="text"
                      className="dark-input"
                      placeholder="Specify other seating style"
                      value={formData.seatingStyleOther || ''}
                      onChange={(e) => handleInputChange('seatingStyleOther', e.target.value)}
                    />
                  )}
                </Col>
              </Row>
            </Form.Group>
          </div>
          
          {/* Technical Requirements */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Technical Requirements</h5>
            
            {technicalFields.map((field) => (
              <Form.Group className="mb-3" key={field.id}>
                <Form.Label className="text-white">{field.label}</Form.Label>
                <Form.Control
                  type="text"
                  className="dark-input"
                  placeholder={field.placeholder}
                  value={formData[`technical_${field.id}`] || ''}
                  onChange={(e) => handleInputChange(`technical_${field.id}`, e.target.value)}
                />
              </Form.Group>
            ))}
          </div>
          
          {/* Space Requirements */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Space Requirements</h5>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Main event space size</Form.Label>
              <Row>
                <Col md={8}>
                  <Form.Control
                    type="number"
                    className="dark-input"
                    placeholder="Size of main space"
                    value={formData.mainSpaceSize || ''}
                    onChange={(e) => handleInputChange('mainSpaceSize', e.target.value)}
                  />
                </Col>
                <Col md={4}>
                  <Form.Select
                    className="dark-input"
                    value={formData.mainSpaceSizeUnit || 'sqft'}
                    onChange={(e) => handleInputChange('mainSpaceSizeUnit', e.target.value)}
                  >
                    <option value="sqft">sq ft</option>
                    <option value="sqm">sq m</option>
                  </Form.Select>
                </Col>
              </Row>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Additional spaces needed</Form.Label>
              <Row>
                {additionalSpacesOptions.slice(0, 8).map((option) => (
                  <Col md={6} key={option.id} className="mb-2">
                    <Form.Check
                      type="checkbox"
                      id={`space-${option.id}`}
                      className="dark-checkbox"
                      label={option.label}
                      checked={isSelected('additionalSpaces', option.id)}
                      onChange={(e) => handleMultiSelectChange('additionalSpaces', option.id, e.target.checked)}
                    />
                    {option.id === 'breakout' && isSelected('additionalSpaces', 'breakout') && (
                      <Form.Control
                        type="number"
                        size="sm"
                        className="dark-input ml-4 mt-1"
                        style={{ width: '60%', marginLeft: '1.5rem' }}
                        placeholder="Number of rooms"
                        value={formData.breakoutRooms || ''}
                        onChange={(e) => handleInputChange('breakoutRooms', e.target.value)}
                      />
                    )}
                  </Col>
                ))}
              </Row>
              
              <Row className="mt-2">
                <Col md={6} className="mb-2">
                  <Form.Check
                    type="checkbox"
                    id="space-other"
                    className="dark-checkbox"
                    label="Other"
                    checked={isSelected('additionalSpaces', 'other')}
                    onChange={(e) => handleMultiSelectChange('additionalSpaces', 'other', e.target.checked)}
                  />
                </Col>
                <Col md={6}>
                  {isSelected('additionalSpaces', 'other') && (
                    <Form.Control
                      type="text"
                      className="dark-input"
                      placeholder="Specify other space needed"
                      value={formData.additionalSpacesOther || ''}
                      onChange={(e) => handleInputChange('additionalSpacesOther', e.target.value)}
                    />
                  )}
                </Col>
              </Row>
            </Form.Group>
          </div>
          
          {/* Venue Amenities */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Venue Amenities</h5>
            
            <Row>
              {venueAmenitiesOptions.slice(0, 1).map((option) => (
                <Col md={6} key={option.id} className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id={`amenity-${option.id}`}
                    className="dark-checkbox"
                    label={option.label}
                    checked={isSelected('venueAmenities', option.id)}
                    onChange={(e) => handleMultiSelectChange('venueAmenities', option.id, e.target.checked)}
                  />
                </Col>
              ))}
              
              <Col md={6} className="mb-3">
                <div className="d-flex align-items-center">
                  <Form.Check
                    type="checkbox"
                    id="amenity-parking"
                    className="dark-checkbox me-2"
                    label="Parking capacity:"
                    checked={isSelected('venueAmenities', 'parking')}
                    onChange={(e) => handleMultiSelectChange('venueAmenities', 'parking', e.target.checked)}
                  />
                  {isSelected('venueAmenities', 'parking') && (
                    <Form.Control
                      type="number"
                      size="sm"
                      className="dark-input"
                      style={{ width: '100px' }}
                      placeholder="# vehicles"
                      value={formData.parkingCapacity || ''}
                      onChange={(e) => handleInputChange('parkingCapacity', e.target.value)}
                    />
                  )}
                </div>
              </Col>
            </Row>
            
            <Row>
              {venueAmenitiesOptions.slice(2, venueAmenitiesOptions.length - 1).map((option) => (
                <Col md={6} key={option.id} className="mb-2">
                  <Form.Check
                    type="checkbox"
                    id={`amenity-${option.id}`}
                    className="dark-checkbox"
                    label={option.label}
                    checked={isSelected('venueAmenities', option.id)}
                    onChange={(e) => handleMultiSelectChange('venueAmenities', option.id, e.target.checked)}
                  />
                </Col>
              ))}
            </Row>
            
            <Row className="mt-2">
              <Col md={6} className="mb-2">
                <Form.Check
                  type="checkbox"
                  id="amenity-other"
                  className="dark-checkbox"
                  label="Other"
                  checked={isSelected('venueAmenities', 'other')}
                  onChange={(e) => handleMultiSelectChange('venueAmenities', 'other', e.target.checked)}
                />
              </Col>
              <Col md={6}>
                {isSelected('venueAmenities', 'other') && (
                  <Form.Control
                    type="text"
                    className="dark-input"
                    placeholder="Specify other amenity"
                    value={formData.venueAmenitiesOther || ''}
                    onChange={(e) => handleInputChange('venueAmenitiesOther', e.target.value)}
                  />
                )}
              </Col>
            </Row>
          </div>
          
          {/* Venue Exclusions */}
          <Form.Group className="mb-4">
            <Form.Label className="text-white">Venue Exclusions</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              className="dark-input"
              placeholder="Any venue types or features to avoid?"
              value={formData.venueExclusions || ''}
              onChange={(e) => handleInputChange('venueExclusions', e.target.value)}
            />
          </Form.Group>
          
          <div className="d-flex justify-content-between">
            <Button
              variant="outline-light"
              className="dark-outline-button"
              onClick={onPrev}
            >
              <FontAwesomeIcon icon={faChevronLeft} className="me-2" />
              Back
            </Button>
            <Button
              className="dark-primary-button"
              onClick={handleNext}
            >
              Continue
              <FontAwesomeIcon icon={faChevronRight} className="ms-2" />
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default VenueTechnical;